import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';

export interface DepartureTimeRow {
  geofips: string;
  location_name: string;
  geography_type: string;
  year: number;
  departure_time_bin: string;
  estimate: number;
  percent?: number;
}

const DEPARTURE_TABLES = ['departure_time_to_work', 'db_departure_time_to_work', 'db.departure_time_to_work'];

function geofipsVariants(g: number | string | null | undefined): string[] {
  if (g == null) return [];
  const s = String(g).trim();
  if (!s) return [];
  if (s.length < 7 && s.startsWith('6')) return [s, `0${s}`];
  if (s.length === 7 && s.startsWith('06')) return [s, s.slice(1)];
  return [s];
}

function locationVariants(location: string): string[] {
  const raw = (location || '').trim();
  if (!raw) return [];
  const base = raw.replace(/\s+(city|town)$/i, '').trim();
  return Array.from(new Set([raw, base, `${base} city`, `${base} town`].filter(Boolean)));
}

/** Fetch departure-time-to-work data for a location. */
export async function getDepartureTimeData(
  location: string,
  yearStart?: number,
  yearEnd?: number
): Promise<DepartureTimeRow[]> {
  if (!location) return [];

  const geofips = await supabaseLocationService.getGeofips(location);
  const gfVariants = geofipsVariants(geofips);
  const locVariants = locationVariants(location);

  const applyYearRange = (q: any) => {
    let query = q;
    if (yearStart != null) query = query.gte('year', yearStart);
    if (yearEnd != null) query = query.lte('year', yearEnd);
    return query;
  };

  for (const table of DEPARTURE_TABLES) {
    const base = supabaseClient
      .from(table)
      .select('geofips, location_name, geography_type, year, departure_time_bin, estimate, percent')
      .order('year', { ascending: true })
      .order('departure_time_bin', { ascending: true });

    // 1) Try place rows by exact location variants first
    for (const name of locVariants) {
      const { data, error } = await applyYearRange(
        base
          .in('geography_type', ['place', 'Place', 'city', 'City'])
          .eq('location_name', name)
      );
      if (!error && data?.length) return data as DepartureTimeRow[];
    }

    // 2) Fuzzy match by place/city names
    for (const name of locVariants) {
      const { data, error } = await applyYearRange(
        base
          .in('geography_type', ['place', 'Place', 'city', 'City'])
          .ilike('location_name', `%${name}%`)
      );
      if (!error && data?.length) return data as DepartureTimeRow[];
    }

    // 3) geofips variants (handles 0606000 vs 606000)
    for (const gf of gfVariants) {
      const { data, error } = await applyYearRange(base.eq('geofips', gf));
      if (!error && data?.length) return data as DepartureTimeRow[];
      if (error) console.error(`departureTimeService: ${table} geofips query`, error);
    }

    // 4) final fuzzy fallback in this table
    const { data, error } = await applyYearRange(base.ilike('location_name', `%${location}%`));
    if (!error && data?.length) return data as DepartureTimeRow[];
    if (error) console.error(`departureTimeService: ${table} fallback query`, error);
  }
  return [];
}

